import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as fbSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { addDoc, collection, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  auth,
  configError,
  db,
  functions,
  googleProvider,
  microsoftProvider,
} from '../firebase';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import { chooseTenantId, setResolvedTenantIds } from '../lib/tenant';
import type { TenantMembership, UserIdentity, UserProfile } from '../types';

/** Read by the sign-in screen once, to explain why the session ended. */
export const SIGNED_OUT_REASON_KEY = 'compass:signedOutReason';

interface AuthContextValue {
  firebaseUser: FirebaseUser | null;
  profile: UserProfile | null;
  /** true while we don't yet know auth state or are loading the profile. */
  loading: boolean;
  /** signed in with a provider but no matching users/ doc exists. */
  unprovisioned: boolean;
  /** Every tenant this account holds a membership in, active tenant first
   *  (tenantIds[0] — see src/lib/tenant.ts's chooseTenantId()). Length <= 1
   *  for almost every real account today; a switcher only needs to render
   *  when it's more than 1 (see switchTenant below). */
  tenantIds: string[];
  /** Phase 5D — reorders the caller's own tenantIds so `tenantId` becomes
   *  active (functions/src/tenantSwitch.ts). Throws if the caller has no
   *  membership in that tenant. Propagates live: writing tenantIds re-fires
   *  the identity listener this provider already holds, so profile/
   *  tenantIds update with no reload and no separate state to manage here. */
  switchTenant: (tenantId: string) => Promise<void>;
  signInGoogle: () => Promise<void>;
  signInMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [tenantIds, setTenantIds] = useState<string[]>([]);
  const [authKnown, setAuthKnown] = useState(false);
  // Guards the once-per-login lastLoginAt write so it can't feed its own snapshot.
  const loginStampedFor = useRef<string | null>(null);
  const [profileKnown, setProfileKnown] = useState(false);
  // Phase 5A: tracks whether resolveActiveTenantId() (src/lib/tenant.ts)
  // has been populated for the current auth state — `loading` below stays
  // true until this settles too, so nothing can call it too early.
  const [tenantKnown, setTenantKnown] = useState(false);

  useEffect(() => {
    if (configError) {
      setAuthKnown(true);
      setProfileKnown(true);
      return;
    }
    return onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user);
      setAuthKnown(true);
      if (!user) {
        setProfile(null);
        setProfileKnown(true);
        setResolvedTenantIds(null);
        setTenantIds([]);
        setTenantKnown(true);
      } else {
        setProfileKnown(false);
        setTenantKnown(false);
      }
    });
  }, []);

  // Phase 5C: identity -> active tenant -> membership resolution chain.
  // `profile` (the thing every authorization helper in src/lib/visibility.ts
  // consumes) is now built by merging TWO docs, never read from the legacy
  // users/{email} plane:
  //   - users/{uid} (UserIdentity) — email/displayName/phone/notes/
  //     tenantIds. Identity/contact info only, never authorization.
  //   - tenantMemberships/{activeTenantId}__{uid} (TenantMembership) —
  //     role/districtId/positions/committees/districtView/keyThree/
  //     councilWide/contactId, for the ACTIVE tenant only (chooseTenantId()
  //     — src/lib/tenant.ts). A person's role in a tenant that ISN'T
  //     active contributes nothing here, by construction.
  // AWAITS claimLegacyProfile before subscribing, so a not-yet-claimed
  // legacy (or newly tenantInvites-granted) user's users/{uid} +
  // tenantMemberships docs exist before this reads them, resolving
  // correctly on this very login rather than only the next one.
  useEffect(() => {
    if (configError || !firebaseUser) return;
    let cancelled = false;
    let unsubIdentity: (() => void) | undefined;
    let unsubMembership: (() => void) | undefined;

    void (async () => {
      try {
        await httpsCallable(functions, 'claimLegacyProfile')();
      } catch {
        // Expected to no-op/error for an already-claimed user or one with
        // nothing pending to claim at all.
      }
      if (cancelled) return;

      unsubIdentity = onSnapshot(
        doc(db, 'users', firebaseUser.uid),
        (idSnap) => {
          unsubMembership?.();
          unsubMembership = undefined;

          if (!idSnap.exists()) {
            setResolvedTenantIds([]);
            setTenantIds([]);
            setTenantKnown(true);
            setProfile(null);
            setProfileKnown(true);
            return;
          }
          const identity = idSnap.data() as Omit<UserIdentity, 'id'>;
          const identityTenantIds = identity.tenantIds ?? [];
          setResolvedTenantIds(identityTenantIds);
          setTenantIds(identityTenantIds);
          setTenantKnown(true);

          const activeTenantId = chooseTenantId(identityTenantIds);
          if (!activeTenantId) {
            setProfile(null);
            setProfileKnown(true);
            return;
          }

          unsubMembership = onSnapshot(
            doc(db, 'tenantMemberships', `${activeTenantId}__${firebaseUser.uid}`),
            (mSnap) => {
              if (!mSnap.exists()) {
                setProfile(null);
                setProfileKnown(true);
                return;
              }
              const m = mSnap.data() as Omit<TenantMembership, 'id'>;
              setProfile({
                id: firebaseUser.uid,
                email: identity.email,
                role: m.role,
                districtId: m.districtId ?? null,
                positions: m.positions ?? [],
                positionIds: m.positionIds ?? [],
                committees: m.committees ?? null,
                districtView: m.districtView ?? null,
                keyThree: m.keyThree ?? null,
                councilWide: m.councilWide ?? null,
                councilAccess: m.councilAccess ?? null,
                contactAccess: m.contactAccess ?? null,
                contactId: m.contactId ?? null,
                personId: m.personId ?? null,
                displayName: identity.displayName ?? undefined,
                phone: identity.phone ?? null,
                notes: identity.notes ?? null,
                createdAt: identity.createdAt,
                lastLoginAt: identity.lastLoginAt,
              });
              setProfileKnown(true);
            },
            () => {
              setProfile(null);
              setProfileKnown(true);
            },
          );

          // Stamp last-login exactly once per login, and backfill the
          // display name from the sign-in provider the first time (if an
          // admin didn't already set one) — on the uid-keyed identity doc
          // now, not the legacy per-email doc. Writing to a doc we're
          // subscribed to re-fires the identity listener above, so the
          // ref guard stops it from looping. Best-effort; ignored if
          // rules forbid it.
          if (loginStampedFor.current !== firebaseUser.uid) {
            loginStampedFor.current = firebaseUser.uid;
            const patch: Record<string, unknown> = { lastLoginAt: serverTimestamp() };
            const providerName = firebaseUser.displayName?.trim();
            if (providerName && !identity.displayName) patch.displayName = providerName;
            void setDoc(doc(db, 'users', firebaseUser.uid), patch, { merge: true }).catch(() => {});

            // Audit log entry for this sign-in — immutable, admin-read-only
            // (see firestore.rules). Same guard, so one entry per login, not
            // one per snapshot.
            void addDoc(collection(db, 'loginEvents'), {
              email: identity.email,
              displayName: firebaseUser.displayName ?? identity.displayName ?? null,
              provider: firebaseUser.providerData[0]?.providerId ?? 'unknown',
              tenantId: activeTenantId,
              at: serverTimestamp(),
            }).catch(() => {});
          }
        },
        () => {
          setResolvedTenantIds([]);
          setTenantIds([]);
          setTenantKnown(true);
          setProfile(null);
          setProfileKnown(true);
        },
      );
    })();

    return () => {
      cancelled = true;
      unsubIdentity?.();
      unsubMembership?.();
    };
  }, [firebaseUser]);

  // Auto sign-out after 4 hours with no mouse/keyboard/touch/scroll activity
  // — session data (roster, contacts) stays live in memory otherwise, with
  // no natural expiry of its own. Stamp a reason so the sign-in screen can
  // explain it; a hard signOut() also drops the Firestore listeners.
  const onInactivityTimeout = useCallback(() => {
    try {
      sessionStorage.setItem(SIGNED_OUT_REASON_KEY, 'inactivity');
    } catch {
      // best-effort — private-browsing or storage-blocked; sign-out still proceeds
    }
    void fbSignOut(auth);
  }, []);
  useInactivityLogout(!!firebaseUser, onInactivityTimeout);

  // Phase 5D — thin wrapper around the switchActiveTenant callable. Never
  // touches local state directly: the write it triggers lands on
  // users/{uid}, which the identity onSnapshot above is already
  // subscribed to, so profile/tenantIds update on their own once the
  // reordered tenantIds comes back through that same listener.
  const switchTenant = useCallback(async (tenantId: string) => {
    await httpsCallable(functions, 'switchActiveTenant')({ tenantId });
  }, []);

  const value: AuthContextValue = {
    firebaseUser,
    profile,
    loading: !authKnown || (!!firebaseUser && (!profileKnown || !tenantKnown)),
    unprovisioned: authKnown && profileKnown && !!firebaseUser && !profile,
    tenantIds,
    switchTenant,
    signInGoogle: async () => {
      await signInWithPopup(auth, googleProvider);
    },
    signInMicrosoft: async () => {
      await signInWithPopup(auth, microsoftProvider);
    },
    signOut: async () => {
      await fbSignOut(auth);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
